import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import type { ColumnsType } from "antd/es/table";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { Text } = Typography;

type HotelProperty = {
  id: number;
  name: string;
};

type Currency = {
  id: number;
  name: string;
  code: string;
  symbol?: string | null;
};

type Tax = {
  id: number;
  name: string;
  rate?: number | string | null;
};

type HotelService = {
  id: number;
  property_id: number;
  property_name?: string | null;
  name: string;
  category: string;
  default_price: number | string;
  currency_id?: number | null;
  tax_id?: number | null;
  is_active?: number | boolean | null;
};

type ServiceFormValues = {
  propertyId: number;
  name: string;
  category: string;
  defaultPrice: number;
  currencyId?: number;
  taxId?: number;
  isActive?: boolean;
};

const serviceCategoryLabels: Record<string, string> = {
  spa: "Spa",
  restaurant: "Restaurant",
  minibar: "Minibar",
  laundry: "Blanchisserie",
  transport: "Transport",
  housekeeping: "Menage",
  other: "Autre",
};

const ServicesTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [services, setServices] = useState<HotelService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [filters, setFilters] = useState({
    propertyId: "all" as number | "all",
    category: "all" as string | "all",
    isActive: "all" as "all" | "true" | "false",
    search: "",
  });
  const [editingService, setEditingService] = useState<HotelService | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm<ServiceFormValues>();

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);
  const canCreate = Boolean(hasPermission?.("Hotellerie.Services.create"));
  const canEdit = Boolean(hasPermission?.("Hotellerie.Services.edit"));
  const canDelete = Boolean(hasPermission?.("Hotellerie.Services.delete"));
  const hasProperties = properties.length > 0;

  const currencyMap = useMemo(() => {
    const map = new Map<number, Currency>();
    currencies.forEach((currency) => map.set(currency.id, currency));
    return map;
  }, [currencies]);

  const taxMap = useMemo(() => {
    const map = new Map<number, Tax>();
    taxes.forEach((tax) => map.set(tax.id, tax));
    return map;
  }, [taxes]);

  const formatPrice = useCallback(
    (value: number | string, currencyId?: number | null) => {
      const numeric = Number(value || 0);
      const formatted = new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numeric);
      if (!currencyId) {
        return formatted;
      }
      const currency = currencyMap.get(currencyId);
      if (!currency) {
        return formatted;
      }
      return `${formatted} ${currency.symbol || currency.code}`;
    },
    [currencyMap]
  );

  const fetchProperties = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/properties`, {
        params,
      });
      if (response.data.success) {
        setProperties(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching properties:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des proprietes"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchCurrencies = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = { page: 1, limit: 200 };
      if (selectedCompany) {
        params.companyId = selectedCompany;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/currencies`, {
        params,
      });
      setCurrencies(response.data.currencies || []);
    } catch (error: any) {
      console.error("[HOTEL] Error fetching currencies:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des devises"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchTaxes = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = { status: "active", limit: 2000 };
      if (selectedCompany) {
        params.companyId = selectedCompany;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/taxes`, { params });
      setTaxes(response.data.taxes || []);
    } catch (error: any) {
      console.error("[HOTEL] Error fetching taxes:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des taxes"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchServices = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingServices(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (filters.propertyId !== "all") {
        params.property_id = filters.propertyId;
      }
      if (filters.category !== "all") {
        params.category = filters.category;
      }
      if (filters.isActive !== "all") {
        params.is_active = filters.isActive;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/services`, {
        params,
      });
      if (response.data.success) {
        setServices(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching services:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des services"
      );
    } finally {
      setLoadingServices(false);
    }
  }, [filters.category, filters.isActive, filters.propertyId, requiresCompanySelection, selectedCompany]);

  useEffect(() => {
    fetchProperties();
    fetchCurrencies();
    fetchTaxes();
  }, [fetchCurrencies, fetchProperties, fetchTaxes]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const filteredServices = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    if (!search) {
      return services;
    }
    return services.filter((service) =>
      [service.name, service.property_name, service.category]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [filters.search, services]);

  const openCreateModal = () => {
    setEditingService(null);
    form.resetFields();
    form.setFieldsValue({
      category: "other",
      defaultPrice: 0,
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (service: HotelService) => {
    setEditingService(service);
    form.setFieldsValue({
      propertyId: service.property_id,
      name: service.name,
      category: service.category,
      defaultPrice: Number(service.default_price || 0),
      currencyId: service.currency_id || undefined,
      taxId: service.tax_id || undefined,
      isActive: Boolean(service.is_active),
    });
    setIsModalOpen(true);
  };

  const handleSaveService = async () => {
    const values = await form.validateFields();
    if (!editingService && !canCreate) {
      message.warning("Vous n'avez pas les droits pour creer un service.");
      return;
    }
    if (editingService && !canEdit) {
      message.warning("Vous n'avez pas les droits pour modifier ce service.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      name: values.name,
      category: values.category,
      default_price: values.defaultPrice ?? 0,
      currency_id: values.currencyId || null,
      tax_id: values.taxId || null,
      is_active: values.isActive ? 1 : 0,
    };

    try {
      if (editingService) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/services/${editingService.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Service mis a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/services`,
          payload
        );
        if (response.data.success) {
          message.success("Service cree.");
        }
      }
      setIsModalOpen(false);
      setEditingService(null);
      fetchServices();
    } catch (error: any) {
      console.error("[HOTEL] Error saving service:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde du service"
      );
    }
  };

  const confirmDeleteService = (service: HotelService) => {
    if (!canDelete) {
      message.warning("Vous n'avez pas les droits pour supprimer ce service.");
      return;
    }
    Modal.confirm({
      title: "Supprimer le service",
      content: `Confirmer la suppression de ${service.name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/services/${service.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Service supprime.");
            fetchServices();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting service:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression du service"
          );
        }
      },
    });
  };

  const columns: ColumnsType<HotelService> = [
    {
      title: "Service",
      key: "service",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary">
            {serviceCategoryLabels[record.category] || record.category}
          </Text>
        </Space>
      ),
    },
    {
      title: "Propriete",
      dataIndex: "property_name",
      key: "property_name",
      render: (value: string | null, record) =>
        value || `Propriete #${record.property_id}`,
    },
    {
      title: "Prix par defaut",
      dataIndex: "default_price",
      key: "default_price",
      render: (value: number | string, record) =>
        formatPrice(value, record.currency_id),
    },
    {
      title: "Taxe",
      dataIndex: "tax_id",
      key: "tax_id",
      render: (value: number | null) =>
        value ? taxMap.get(value)?.name || `Taxe #${value}` : "-",
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (value: number | boolean | null) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          {canEdit ? (
            <Button type="link" onClick={() => openEditModal(record)}>
              Modifier
            </Button>
          ) : null}
          {canDelete ? (
            <Button type="link" danger onClick={() => confirmDeleteService(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder aux services."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title="Services"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchServices}
              loading={loadingServices}
            >
              Rafraichir
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              disabled={!canCreate || !hasProperties}
            >
              Nouveau service
            </Button>
          </Space>
        }
      >
        {!hasProperties ? (
          <Alert
            message="Aucune propriete configuree"
            description="Creez une propriete dans l'onglet Configuration pour activer la creation de services."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Form.Item label="Propriete">
                <Select
                  value={filters.propertyId}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, propertyId: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...properties.map((property) => ({
                      value: property.id,
                      label: property.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Categorie">
                <Select
                  value={filters.category}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, category: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...Object.keys(serviceCategoryLabels).map((key) => ({
                      value: key,
                      label: serviceCategoryLabels[key],
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Actif">
                <Select
                  value={filters.isActive}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, isActive: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "true", label: "Actif" },
                    { value: "false", label: "Inactif" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Recherche">
                <Input
                  placeholder="Nom, categorie"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          columns={columns}
          dataSource={filteredServices}
          rowKey="id"
          loading={loadingServices}
          pagination={{ pageSize: 8 }}
        />
      </Card>

      <Modal
        title={editingService ? "Modifier service" : "Nouveau service"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingService(null);
        }}
        onOk={handleSaveService}
        okText="Enregistrer"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select
              disabled={Boolean(editingService)}
              options={properties.map((property) => ({
                value: property.id,
                label: property.name,
              }))}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Nom"
                name="name"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Categorie" name="category">
                <Select
                  options={Object.keys(serviceCategoryLabels).map((key) => ({
                    value: key,
                    label: serviceCategoryLabels[key],
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Prix par defaut" name="defaultPrice">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Devise" name="currencyId">
                <Select
                  allowClear
                  options={currencies.map((currency) => ({
                    value: currency.id,
                    label: `${currency.code} - ${currency.name}`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Taxe" name="taxId">
                <Select
                  allowClear
                  options={taxes.map((tax) => ({
                    value: tax.id,
                    label: tax.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Actif" name="isActive" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Space>
  );
};

export default ServicesTab;
